import DOMPurify from "dompurify"
import { format } from "date-fns"
import { toNumber } from "lodash"
import jsonLogic from "json-logic-js"

// Apollo
import { GET_ALL_ROLE_DOC_OPTIONS } from "Pages/Referentials/graphql/queries/getAllRoleDocOptions"

// Utils
import { getFormatDate } from "Shared/utils/date.utils"

export interface ScaleItem {
  key: string
  value: number
  description?: string
}

export interface ConditionalLabelSet {
  condition: any
  labels: ScaleItem[]
  enumColors?: Record<string, { backgroundColor?: string; color?: string }>
}

/**
 * Normalise les valeurs de champs date dans rowData pour l'évaluation JsonLogic.
 *
 * Problème : les champs date sont stockés en ISO string ("2026-01-01T00:00:00.000Z") et convertis
 * en objets Date JS par transformItemWithScale. JsonLogic compare alors le timestamp (1 735 689 600 000)
 * à une année (2026), ce qui rend toute comparaison sémantique impossible.
 *
 * Solution : pour chaque champ date, convertir la valeur en primitif comparable :
 *   - format_date "yyyy"      → année entière (ex : 2026)
 *   - autres formats date     → timestamp ms (pour comparaisons relatives)
 *
 * @param rowData   Données brutes de la ligne AG Grid
 * @param dateMeta  Map field → format_date (extraite des cellRendererParams des colonnes)
 */
export function normalizeRowDataForConditions(
  rowData: Record<string, any> | null | undefined,
  dateMeta: Record<string, string>
): Record<string, any> | null | undefined {
  if (!rowData || !Object.keys(dateMeta).length) return rowData
  const result = { ...rowData }
  for (const [field, fmt] of Object.entries(dateMeta)) {
    if (!(field in result) || !result[field]) continue
    const raw = result[field]
    const d = raw instanceof Date ? raw : new Date(raw as any)
    if (isNaN(d.getTime())) continue
    if (fmt === "yyyy") {
      result[field] = d.getUTCFullYear()
    } else {
      result[field] = d.getTime()
    }
  }
  return result
}

/**
 * Résout le jeu de labels applicable à une cellule Scale selon les données de la ligne.
 * Évalue chaque condition JsonLogic dans l'ordre ; retourne les labels du premier match.
 * Si aucune condition ne correspond (ou s'il n'y a pas de données contextuelles), retourne `defaultScale`.
 */
export function resolveScale(
  defaultScale: ScaleItem[],
  conditionalLabels?: ConditionalLabelSet[] | null,
  rowData?: Record<string, any> | null
): ScaleItem[] {
  if (!conditionalLabels?.length || !rowData) return defaultScale ?? []

  for (const { condition, labels } of conditionalLabels) {
    try {
      if (jsonLogic.apply(condition, rowData)) return labels
    } catch {
      // condition invalide → ignorer, passer à la suivante
    }
  }

  return defaultScale ?? []
}

/**
 * Résout la map de couleurs applicable à une cellule Scale selon les données de la ligne.
 * Évalue chaque condition JsonLogic dans l'ordre ; retourne les couleurs du premier match qui en a.
 * Si aucune condition ne correspond, retourne `defaultEnumColors`.
 */
export function resolveEnumColors(
  defaultEnumColors: Record<string, { backgroundColor?: string; color?: string }> | undefined,
  conditionalLabels?: ConditionalLabelSet[] | null,
  rowData?: Record<string, any> | null
): Record<string, { backgroundColor?: string; color?: string }> | undefined {
  if (!conditionalLabels?.length || !rowData) return defaultEnumColors

  for (const { condition, enumColors } of conditionalLabels) {
    try {
      if (jsonLogic.apply(condition, rowData)) return enumColors ?? defaultEnumColors
    } catch {
      // condition invalide → ignorer, passer à la suivante
    }
  }

  return defaultEnumColors
}

export function htmlToText(html: string) {
  if (typeof html === "string") {
    let text = html
      // Replace closing tags with newlines
      .replace(/<\/p>|<\/li>|<br\s*\/?>/gi, "\n")
      // Replace opening <li> with "- "
      .replace(/<li[^>]*>/gi, "- ")
      // Replace &nbsp; with space
      .replace(/&nbsp;/gi, " ")
      // Remove any remaining HTML tags
      .replace(/<[^>]+>/g, "")
      .trim();
    // Remove extra blank lines
    return text.replace(/\n{2,}/g, "\n");
  } else {
    return html
  }
}

export const handleGetSelectValue = (optionsList: any, valueId: string) => {
  if (Array.isArray(valueId)) {
    return optionsList?.filter((opt: any) => valueId.includes(opt.value))
  } else {
    return optionsList?.find((opt: any) => opt?.value === valueId)
  }
}

export const transformFormatByDataType = (colDef: any, value: any) => {
  const dt = colDef?.cellRendererParams?.dataType ?? colDef?.dataType
  const fmt = colDef?.cellRendererParams?.format_date ?? colDef?.format_date

  if (dt === "stringHtml") {
    return htmlToText(DOMPurify.sanitize(value))
  } else if (dt === "date") {
    return getFormatDate(value, fmt)
  } else if (colDef?.scale) {
    if (typeof value === "number") {
      return colDef?.scale[value - 1]?.key
    } else {
      return colDef?.scale[value]?.key
    }
  } else {
    return value
  }
}

export const removeUuidFromFilename = (filename: string) => {
  // Définir l'expression régulière pour l'UUID
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/
  // Supprimer l'UUID du nom de fichier
  return filename?.replace(uuidRegex, "")
}

export const getIconFile = (file: any) => {
  const extension = file.split(".").pop()
  switch (extension) {
    case "csv":
      return "fa-file-csv"
    case "xls":
    case "xlsx":
      return "fa-file-xls"
    case "pdf":
      return "fa-file-pdf"
    case "jpg":
    case "jpeg":
      return "fa-file-jpg"
    case "png":
      return "fa-file-png"
    case "svg":
      return "fa-file-svg"
    case "doc":
    case "docx":
      return "fa-file-doc"
    default:
      return "fa-file"
  }
}

/**
 * Format une valeur en fonction de sa propriété jsonForm
 * @param client Apollo client, nécessaire pour query les rolesDocs des relations
 * @param property Propriété de la colonne (uxComponent => jsonForms => properties)
 * @param rolesDocs RolesDocs du référentiel associé
 * @param value
 * @param relationReferentialId Id du referential de la relation
 * @returns La valeur formatée
 */
export async function formatDataByProperty(
  client: any,
  property: Record<string, any>,
  rolesDocs: Record<string, any>,
  value: any,
  relationReferentialId?: string
) {
  const isUserValue =
    typeof value === "string" ? value?.startsWith("user:") || value?.startsWith("group:") : false

  if (isUserValue) {
    // Si c'est une relation récupérer les rolesDocs associés
    if (relationReferentialId) {
      // `formatDataByProperty` est appelée par valeur formatée : en `network-only`, chaque
      // cellule `user:`/`group:` d'une relation déclenchait son propre aller-retour vers
      // `getAllRoleDocOptions`. `cache-first` mutualise sur (référentiel de la relation).
      // NB: `skip` n'existe pas sur `client.query` (option `useQuery`) — retiré, sans effet.
      const { data: dtRoles } = await client.query({
        query: GET_ALL_ROLE_DOC_OPTIONS,
        variables: { idReferential: relationReferentialId },
        fetchPolicy: "cache-first",
      })

      const relationRoleDocs = dtRoles?.getAllRoleDocOptions
      const roleDocValue = relationRoleDocs?.[property?.roleDoc]?.find(
        (item: Record<string, any>) => item.value === value
      )?.label

      return roleDocValue
    }

    // Sinon se servir de ceux envoyé par la Card
    const roleDocValue = rolesDocs?.[property?.roleDoc]?.find(
      (item: Record<string, any>) => item.value === value
    )?.label

    return roleDocValue
  } else if (property?.oneOf) {
    const valueIndex = toNumber(value)
    return property?.oneOf?.[1]?.enum?.[valueIndex - 1]
  } else if (property?.type === "array") {
    return value?.join(", ")
    // return value?.map((v: any) => formatDataByProperty(client, property?.items, v))?.join(", ")
  } else if (property?.format_date) {
    return getFormatDate(value, property.format_date)
  } else if (property?.input?.type === "stringHtml") {
    return htmlToText(DOMPurify.sanitize(value))
  } else if (property?.type === "boolean") {
    return value ? "Oui" : "Non"
  } else {
    return value
  }
}
